/**
 * Helper (pure) ของ error ที่ API layer คืนมา — `handleApiError` (MeGenInterface) แปลง axios error ทุกแบบเป็น `ApiError`
 *
 * - `status` เป็นเลข HTTP เฉพาะเมื่อ response มาถึงจริง · `null` = network / timeout / ไม่ใช่ axios error
 * - แยกออกมาที่นี่เพราะ handler (MeGenInterface) และหน้าจอ (CardGenerateDirectScript) ต้องตัดสินจาก status
 *   ด้วยกฎเดียวกัน และ MeGenInterface import ในเทสต์ node ไม่ได้ (ดึง axios/CONFIG ตอน import)
 */

export type ApiError = {
  status: number | null;
  message: string;
  data?: any;
};

const CLIENT_ERROR_MIN = 400;
const CLIENT_ERROR_MAX = 499;
const HTTP_NOT_FOUND = 404;

/** 4xx = หลังบ้าน "ปฏิเสธ" คำขอนี้ชัดเจน (validation / สิทธิ์ / ไม่พบ) — ยิงซ้ำก็ได้ผลเดิม ไม่ใช่อาการชั่วคราว */
export const isClientError = (err: Pick<ApiError, "status"> | null | undefined): boolean =>
  typeof err?.status === "number" && err.status >= CLIENT_ERROR_MIN && err.status <= CLIENT_ERROR_MAX;

/** 404 — บาง endpoint แปลว่า "ยังไม่มีของ" (เช่น manifest ของ scene ที่ยังไม่เคย generate) ไม่ใช่ error */
export const isNotFound = (err: Pick<ApiError, "status"> | null | undefined): boolean =>
  err?.status === HTTP_NOT_FOUND;

/** ข้อความสำรองเมื่อแกะข้อความจาก error ไม่ได้เลย (รายละเอียดจริงอยู่ใน console จาก handleApiError) */
export const UNKNOWN_ERROR_TEXT = "เกิดข้อผิดพลาด กรุณาลองใหม่";

const textOf = (value: unknown): string | null => {
  if (value == null) return null;
  if (typeof value === "string") return value.trim() ? value : null;
  if (Array.isArray(value)) {
    const parts = value.map(textOf).filter((s): s is string => s !== null);
    return parts.length ? parts.join(" | ") : null;
  }
  if (typeof value === "object") {
    const o = value as Record<string, unknown>;
    // ลำดับตาม handleApiError / FastAPI: message → detail (422 เป็น array ของ {msg}) → error → msg
    return textOf(o.message) ?? textOf(o.detail) ?? textOf(o.error) ?? textOf(o.msg);
  }
  return String(value);
};

/**
 * แปลงอะไรก็ตามที่ถูกส่งเข้า setErrorMessage ให้เป็น string เดียวสำหรับ snack
 * (`ErrorMessage.tsx` render object เป็นแถว key/value → จะโชว์ status/data ดิบ รวมถึง HTML ทั้งหน้าของ error page)
 * `null` = ไม่มีอะไรจะโชว์ (SnackMessage ไม่ render)
 */
export const errorText = (error: unknown): string | null => {
  if (error == null || error === "") return null;
  return textOf(error) ?? UNKNOWN_ERROR_TEXT;
};
